import { useState } from 'react';

import { useElements, useStripe } from '@stripe/react-stripe-js';
import type { CreateTokenCardData, Token } from '@stripe/stripe-js';

const DEFAULT_ERROR = 'An unknown error occurred. Please try again.';

const INITIALIZATION_ERROR = 'The payment form is still initializing. Please try again shortly.';
const ELEMENT_ERROR = '`tokenizeStripeCardElement` called without a "<CardElement />"';

export const useStripeTokenizeCardElement = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [tokenizing, setTokenizing] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const tokenize = async (data: CreateTokenCardData): Promise<Token | undefined> => {
    try {
      setTokenizing(true);
      setError(undefined);
      if (!stripe) throw new Error(INITIALIZATION_ERROR);
      if (!elements) throw new Error(INITIALIZATION_ERROR);

      const element = elements.getElement('card');
      if (!element) throw new Error(ELEMENT_ERROR);

      const result = await stripe.createToken(element, data);

      if (result.error) throw new Error(result.error.message ?? DEFAULT_ERROR);

      return result.token;
    } catch (exception) {
      if (exception instanceof Error) setError(exception.message);
      else setError(DEFAULT_ERROR);
    } finally {
      setTokenizing(false);
    }
  };

  return { tokenize, tokenizing, error };
};
